// Owner home dashboard. At-a-glance KPIs + today's schedule + bottlenecks.
// Replaces Job Inbox as the default landing page so owners walk in and
// immediately see what needs attention today.

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { watchJobs, createTestJob, ownerConfirmGuard, updateJob, jobDisplayNumber } from "../../services/firestore";
import { watchInvoices, payCycleFor, payCycleLabel } from "../../services/invoices";
import { isDaiJob, isOrionDirectJob, directBillsByDate, jobReadyByDate, todayYmd } from "../../services/billingReadiness";
import JobDetailModal from "../../components/owner/JobDetailModal";
import "./OwnerLayout.css";

const TYPE_LABELS = { inbound: "Inbound", outbound: "Outbound", roundtrip: "Round trip", direct: "Direct" };

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function isToday(s) {
  if (!s) return false;
  return s === ymd(new Date());
}
function isTomorrow(s) {
  if (!s) return false;
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return s === ymd(t);
}

function formatGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function formatLongDate() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

function formatMoney(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

export default function Dashboard() {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  // Job to preview in the read-only JobDetailModal. Opened by clicking
  // "Open" on a draft invoice so the owner can drill into the underlying
  // job (route, packages, POD) without leaving the dashboard.
  const [viewJobId, setViewJobId] = useState(null);

  useEffect(() => {
    if (!userProfile?.companyId) return;
    const unsubJobs = watchJobs(userProfile.companyId, null, (data) => {
      setJobs(data);
      setLoading(false);
    });
    const unsubInv = watchInvoices(userProfile.companyId, setInvoices);
    return () => { unsubJobs(); unsubInv(); };
  }, [userProfile?.companyId]);

  // ── KPIs ────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const live = jobs.filter((j) => j.status === "inProgress");
    const todayJobs = jobs.filter((j) => isToday(j.scheduledDate)
      && ["pool","grabbed","confirmed","inProgress","complete"].includes(j.status));
    const tomorrowConfirms = jobs.filter((j) =>
      isTomorrow(j.scheduledDate)
      && j.salesRepPhone
      && !j.repConfirmedAt
      && j.requiresRepConfirm !== false
    );
    const today = todayYmd();
    // Per-job items needing billing: not invoiced, not cancelled, not DAI
    // (DAI is counted as batches below). A job qualifies if a guard marked
    // it complete, OR it's a direct — any broker — whose date is today-or-
    // earlier (same rule as the Ready-to-bill screen, via jobReadyByDate).
    const readyToBill = jobs.filter((j) =>
      j.invoiceStatus !== "invoiced"
      && j.status !== "cancelled"
      && !isDaiJob(j)
      && (j.status === "complete" || (directBillsByDate(j) && jobReadyByDate(j, today)))
    );
    // Orion directs specifically — the subset shown in the "Directs to
    // bill" summary list (sorted oldest-first so the most overdue is on top).
    const orionDirectsToBill = readyToBill
      .filter((j) => isOrionDirectJob(j))
      .slice()
      .sort((a, b) => (a.scheduledDate || "9999").localeCompare(b.scheduledDate || "9999"));
    // Everything else ready to bill that isn't an Orion direct or DAI —
    // i.e. completed or date-ready Malca / Ferrari / Executive (incl.
    // Kallati) jobs. Shown as a single
    // rollup row in the panel so the dashboard still flags them (this is
    // what the old "ready to invoice" alert used to cover) without listing
    // each one.
    const otherReadyToBill = readyToBill.filter((j) => !isOrionDirectJob(j));
    // DAI batches that are fully ready to forward: grouped by bundleId
    // (standalone DAI jobs get a synthetic solo key), every job in the
    // batch ready-by-date, and at least one job not yet invoiced. Mirrors
    // the Ready-to-bill "Bundled (DAI)" tab's forwardable count.
    const daiBundlesReady = (() => {
      const byId = new Map();
      for (const j of jobs) {
        if (!isDaiJob(j)) continue;
        const key = j.bundleId || `solo:${j.id}`;
        const cur = byId.get(key) || { bundleId: key, jobs: [], isSolo: !j.bundleId };
        cur.jobs.push(j);
        byId.set(key, cur);
      }
      const out = [];
      for (const b of byId.values()) {
        const hasUninvoiced = b.jobs.some((j) => j.invoiceStatus !== "invoiced");
        if (!hasUninvoiced) continue; // fully forwarded already
        if (!b.jobs.every((j) => jobReadyByDate(j, today))) continue; // still filling
        const dates = b.jobs.map((j) => j.scheduledDate).filter(Boolean).sort();
        out.push({
          bundleId: b.bundleId,
          isSolo: b.isSolo,
          jobCount: b.jobs.length,
          brokerName: b.jobs[0]?.brokerName || "DAI",
          latest: dates[dates.length - 1] || null,
        });
      }
      return out.sort((a, b) => String(a.latest).localeCompare(String(b.latest)));
    })();
    const currentCycle = payCycleFor(new Date());
    const cycleInvoices = invoices.filter((i) => i.payCycle === currentCycle);
    const cycleBilled = cycleInvoices.reduce((s, i) => s + (Number(i.total) || 0), 0);
    const cyclePaidOut = cycleInvoices.reduce((s, i) => s + (Number(i.guardPaidAmount) || 0), 0);
    const draftInvoices = invoices.filter((i) => i.status === "draft");
    return {
      live, todayJobs, tomorrowConfirms, readyToBill,
      orionDirectsToBill, daiBundlesReady, otherReadyToBill,
      currentCycle, cycleInvoices, cycleBilled, cyclePaidOut,
      draftInvoices,
    };
  }, [jobs, invoices]);

  // ── Alerts (issues that need owner action) ─────────────────────────
  // Each alert carries an `items` array — specific jobs/invoices to act on.
  // Rendered as sub-rows under the alert title with per-item buttons so the
  // owner can resolve from here without bouncing to another page.
  const alerts = useMemo(() => {
    const out = [];

    const released = jobs.filter((j) => j.guardReleased);
    if (released.length) {
      out.push({
        kind: "warn",
        title: `${released.length} job${released.length === 1 ? "" : "s"} released back to pool`,
        subtitle: "Already available for any guard to grab — dismiss when seen, or assign manually.",
        // Default CTA acknowledges the alert and keeps the job in the pool
        // (the slot is already cleared because guardReleaseJob removed the
        // guard from guardAssignments). Owners who want to pull it out and
        // hand-pick someone go through the inbox triage flow.
        items: released.slice(0, 5).map((j) => ({
          id: j.id,
          primary: j.jobNumber || j.brokerName || j.id,
          secondary: `${j.scheduledDate || ""} ${j.scheduledTime || ""}`.trim(),
          cta: "Keep in pool",
          onClick: async () => {
            try {
              await updateJob(j.id, { guardReleased: false });
            } catch (err) {
              alert("Couldn't dismiss: " + (err?.message || err));
            }
          },
        })),
        moreCount: Math.max(0, released.length - 5),
        seeAll: () => navigate("/owner/inbox"),
      });
    }

    const grabbed = jobs.filter((j) => j.status === "grabbed");
    if (grabbed.length) {
      out.push({
        kind: "info",
        title: `${grabbed.length} guard grab${grabbed.length === 1 ? "" : "s"} pending your confirm`,
        subtitle: "A guard claimed the job from the pool — confirm to lock them in.",
        items: grabbed.slice(0, 5).map((j) => {
          const grabbedBy = j.guardAssignments?.find((g) => g.guardId === j.grabbedByGuardId)
            || j.guardAssignments?.[0];
          const guardName = grabbedBy?.guardName || "guard";
          return {
            id: j.id,
            primary: `${j.jobNumber || j.brokerName || "Job"} · grabbed by ${guardName}`,
            secondary: `${j.scheduledDate || ""} ${j.scheduledTime || ""}`.trim(),
            cta: "Confirm",
            onClick: async () => {
              try {
                await ownerConfirmGuard(j.id);
              } catch (err) {
                alert("Couldn't confirm: " + (err?.message || err));
              }
            },
          };
        }),
        moreCount: Math.max(0, grabbed.length - 5),
      });
    }

    // NOTE: the old "jobs ready to invoice" alert used to live here. It's
    // been folded into the dedicated "Directs to bill" panel below (which
    // lists Orion directs, DAI batches, and a rollup for other ready
    // jobs) so there's a single billing reminder instead of two that
    // overlapped.

    if (kpis.draftInvoices.length > 0) {
      out.push({
        kind: "info",
        title: `${kpis.draftInvoices.length} draft invoice${kpis.draftInvoices.length === 1 ? "" : "s"} not yet sent`,
        items: kpis.draftInvoices.slice(0, 3).map((i) => ({
          id: i.id,
          primary: `${i.invoiceNumber} · ${i.brokerName}`,
          secondary: `${i.guardName || ""}${i.total ? ` · $${Number(i.total).toFixed(2)}` : ""}`,
          cta: "Open",
          // Open the underlying job in the detail popup. Falls through to
          // the Invoices list if this invoice has no job on record (e.g.
          // a forward-bundle invoice whose job was never a separate doc).
          onClick: () => (i.jobId ? setViewJobId(i.jobId) : navigate("/owner/invoices")),
        })),
        moreCount: Math.max(0, kpis.draftInvoices.length - 3),
        seeAll: () => navigate("/owner/invoices"),
      });
    }

    return out;
  }, [jobs, kpis, navigate]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{formatGreeting()}, {(userProfile?.name || "").split(" ")[0] || "Owner"}</div>
          <div className="page-subtitle">{formatLongDate()}</div>
        </div>
        <button
          className="btn btn-sm"
          onClick={async () => {
            try {
              await createTestJob(userProfile.companyId, userProfile.id);
              alert("🧪 Test job created — find it in the pool. Pair with the Guard Test Kit doc.");
            } catch (err) {
              alert("Failed to create test job: " + (err?.message || err));
            }
          }}
          title="Create a sandbox test job for guard onboarding"
        >🧪 Create test job</button>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loading-state">Loading dashboard...</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

            {/* KPI cards */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)", gap: 12,
            }}>
              <KpiCard
                label="Live now"
                value={kpis.live.length}
                hint={kpis.live.length === 0 ? "No jobs in progress" : "in progress"}
                color="#0E7C46"
                onClick={() => navigate("/owner/live")}
              />
              <KpiCard
                label="Tomorrow's confirms"
                value={kpis.tomorrowConfirms.length}
                hint={kpis.tomorrowConfirms.length === 0 ? "Nothing to confirm" : "rep texts due today"}
                color={kpis.tomorrowConfirms.length > 0 ? "#a36b1d" : "#888"}
                onClick={() => navigate("/owner/inbox")}
              />
              <KpiCard
                label="Ready to bill"
                value={kpis.readyToBill.length}
                hint={kpis.readyToBill.length === 0 ? "All caught up" : "awaiting review"}
                color={kpis.readyToBill.length > 0 ? "#185FA5" : "#888"}
                onClick={() => navigate("/owner/billing")}
              />
              <KpiCard
                label="This pay cycle"
                value={formatMoney(kpis.cycleBilled)}
                hint={`${payCycleLabel(kpis.currentCycle)}${kpis.cyclePaidOut > 0 ? ` · ${formatMoney(kpis.cycleBilled - kpis.cyclePaidOut)} margin` : ""}`}
                color="#185FA5"
                onClick={() => navigate("/owner/invoices")}
              />
            </div>

            {/* Alerts */}
            {alerts.length > 0 && (
              <div className="panel">
                <div className="panel-header">
                  <div className="panel-title">⚠ Needs attention</div>
                </div>
                {alerts.map((a, i) => (
                  <div key={i} style={{
                    borderBottom: i < alerts.length - 1 ? "1px solid #f5f5f5" : "none",
                    background: a.kind === "warn" ? "#fff7e0" : "#fff",
                  }}>
                    {/* Alert title row */}
                    <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 18 }}>{a.kind === "warn" ? "⚠️" : "ⓘ"}</span>
                      <div style={{ flex: 1, fontSize: 12 }}>
                        <div style={{ fontWeight: 600, color: "#111" }}>{a.title}</div>
                        {a.subtitle && (
                          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{a.subtitle}</div>
                        )}
                      </div>
                    </div>
                    {/* Per-item rows */}
                    {a.items?.map((it) => (
                      <div key={it.id} style={{
                        padding: "8px 14px 8px 44px",
                        display: "flex", alignItems: "center", gap: 12,
                        borderTop: "1px solid rgba(0,0,0,0.04)",
                        fontSize: 12,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {it.primary}
                          </div>
                          {it.secondary && (
                            <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>{it.secondary}</div>
                          )}
                        </div>
                        <button className="btn btn-sm btn-blue" onClick={it.onClick}>
                          {it.cta}
                        </button>
                      </div>
                    ))}
                    {a.moreCount > 0 && (
                      <div
                        onClick={a.seeAll}
                        style={{
                          padding: "6px 14px 10px 44px", fontSize: 11, color: "#185FA5",
                          cursor: a.seeAll ? "pointer" : "default", textDecoration: a.seeAll ? "underline" : "none",
                        }}
                      >
                        + {a.moreCount} more →
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Directs to bill — the at-a-glance "what needs billing" list.
                Orion directs surface by date (today-or-earlier); DAI shows
                as whole batches that are ready to forward. Every row jumps
                into Ready to bill. Hidden entirely when there's nothing
                outstanding so it doesn't add noise on a clean day. */}
            {(kpis.orionDirectsToBill.length > 0 || kpis.daiBundlesReady.length > 0 || kpis.otherReadyToBill.length > 0) && (
              <div className="panel">
                <div className="panel-header">
                  <div>
                    <div className="panel-title">💵 To bill</div>
                    <div style={{ fontSize: 11, color: "#888" }}>
                      {kpis.orionDirectsToBill.length} Orion direct{kpis.orionDirectsToBill.length === 1 ? "" : "s"}
                      {" · "}
                      {kpis.daiBundlesReady.length} DAI batch{kpis.daiBundlesReady.length === 1 ? "" : "es"} ready
                      {kpis.otherReadyToBill.length > 0 && ` · ${kpis.otherReadyToBill.length} other`}
                    </div>
                  </div>
                  <button className="btn btn-sm btn-blue" onClick={() => navigate("/owner/billing")}>
                    Open Ready to bill →
                  </button>
                </div>

                {/* Orion direct rows */}
                {kpis.orionDirectsToBill.map((job) => {
                  const stops = (job.stops || []).filter((s) => s.type !== "airport");
                  const route = stops.length === 0 ? "" :
                    stops.length === 1 ? (stops[0].outOfTown ? "OOT" : stops[0].locationName || "") :
                    `${stops[0].outOfTown ? "OOT" : (stops[0].locationName || "")} → ${stops[stops.length - 1].outOfTown ? "OOT" : (stops[stops.length - 1].locationName || "")}`;
                  // Flag jobs surfaced purely by date (guard never tapped
                  // "complete") so the owner knows to double-check them.
                  const byDateOnly = job.status !== "complete";
                  return (
                    <div key={job.id} style={{
                      padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
                      display: "flex", alignItems: "center", gap: 12, fontSize: 12,
                    }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: "#111", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          <strong>{jobDisplayNumber(job) || job.brokerName || "Job"}</strong>
                          {" · "}
                          <span style={{ color: "#666" }}>{job.brokerName || ""}{job.salesRepName ? ` · ${job.salesRepName}` : ""}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                          {job.scheduledDate || "no date"}{route ? ` · ${route}` : ""}
                          {byDateOnly && (
                            <span style={{ color: "#a36b1d", fontWeight: 700 }}> · not marked complete</span>
                          )}
                        </div>
                      </div>
                      <button className="btn btn-sm btn-blue" onClick={() => navigate("/owner/billing")}>
                        Review
                      </button>
                    </div>
                  );
                })}

                {/* DAI batch rows */}
                {kpis.daiBundlesReady.map((b) => (
                  <div key={b.bundleId} style={{
                    padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
                    display: "flex", alignItems: "center", gap: 12, fontSize: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#111" }}>
                        <span style={{ fontSize: 13 }}>📎</span>{" "}
                        <strong>{b.isSolo ? "DAI job" : "DAI batch"}</strong>
                        {" · "}
                        <span style={{ color: "#666" }}>{b.jobCount} job{b.jobCount === 1 ? "" : "s"} ready to forward</span>
                      </div>
                      {b.latest && (
                        <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                          last job {b.latest}
                        </div>
                      )}
                    </div>
                    <button className="btn btn-sm btn-blue" onClick={() => navigate("/owner/billing")}>
                      Forward
                    </button>
                  </div>
                ))}

                {/* Rollup row for everything else ready to bill (completed
                    Malca / Ferrari / Executive jobs). One line instead of
                    per-job rows — replaces the old "ready to invoice"
                    alert without crowding out the directs. */}
                {kpis.otherReadyToBill.length > 0 && (
                  <div style={{
                    padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
                    display: "flex", alignItems: "center", gap: 12, fontSize: 12,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#111" }}>
                        <span style={{ fontSize: 13 }}>📋</span>{" "}
                        <strong>{kpis.otherReadyToBill.length} other job{kpis.otherReadyToBill.length === 1 ? "" : "s"} ready to bill</strong>
                      </div>
                      <div style={{ fontSize: 10, color: "#888", marginTop: 1 }}>
                        Completed Malca / Ferrari / Executive jobs awaiting invoice
                      </div>
                    </div>
                    <button className="btn btn-sm btn-blue" onClick={() => navigate("/owner/billing")}>
                      Review
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Today's schedule */}
            <div className="panel">
              <div className="panel-header">
                <div className="panel-title">Today's schedule</div>
                <span className="chip chip-gray">{kpis.todayJobs.length} job{kpis.todayJobs.length !== 1 ? "s" : ""}</span>
              </div>
              {kpis.todayJobs.length === 0 ? (
                <div style={{ padding: "16px 14px", fontSize: 12, color: "#888", fontStyle: "italic" }}>
                  Nothing scheduled today.
                </div>
              ) : (
                kpis.todayJobs
                  .slice()
                  .sort((a, b) => (a.scheduledTime || "99:99").localeCompare(b.scheduledTime || "99:99"))
                  .map((job) => {
                    const stops = (job.stops || []).filter((s) => s.type !== "airport");
                    const route = stops.length === 0 ? "" :
                      stops.length === 1 ? stops[0].locationName || "" :
                      `${stops[0].outOfTown ? "OOT" : (stops[0].locationName || "")} → ${stops[stops.length - 1].outOfTown ? "OOT" : (stops[stops.length - 1].locationName || "")}`;
                    const statusChip = {
                      pool:       { label: "In pool", color: "#888", bg: "#f0f0f0" },
                      grabbed:    { label: "Grabbed", color: "#185FA5", bg: "#E6F1FB" },
                      confirmed:  { label: "Confirmed", color: "#185FA5", bg: "#E6F1FB" },
                      inProgress: { label: "In progress", color: "#0E7C46", bg: "#E0F4E8" },
                      complete:   { label: "Complete", color: "#0E7C46", bg: "#E0F4E8" },
                    }[job.status] || { label: job.status, color: "#888", bg: "#f0f0f0" };
                    return (
                      <div key={job.id} style={{
                        display: "grid",
                        gridTemplateColumns: "70px 90px 1fr 130px 120px",
                        gap: 12, padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
                        alignItems: "center", fontSize: 12,
                      }}>
                        <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#185FA5" }}>
                          {job.scheduledTime || "—"}
                        </span>
                        <span style={{ fontSize: 11, textTransform: "uppercase", color: "#666" }}>
                          {TYPE_LABELS[job.jobType] || job.jobType}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600 }}>{job.brokerName || "—"}{job.salesRepName ? ` · ${job.salesRepName}` : ""}</div>
                          {route && <div style={{ fontSize: 11, color: "#888" }}>{route}</div>}
                        </div>
                        <span style={{ fontSize: 11, color: "#666" }}>
                          {(job.guardAssignments || []).map((g) => g.guardName?.split(" ")[0] || `G${g.guardNumber}`).join(", ") || "—"}
                        </span>
                        <span style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 10,
                          color: statusChip.color, background: statusChip.bg,
                          justifySelf: "end",
                        }}>{statusChip.label}</span>
                      </div>
                    );
                  })
              )}
            </div>

            {/* Tomorrow's confirms */}
            {kpis.tomorrowConfirms.length > 0 && (
              <div className="panel">
                <div className="panel-header">
                  <div>
                    <div className="panel-title">📱 Texts to send today</div>
                    <div style={{ fontSize: 11, color: "#888" }}>
                      Per Orion FAQ: confirm with the rep by 11am the day before. Guards see these on My Jobs.
                    </div>
                  </div>
                </div>
                {kpis.tomorrowConfirms.map((job) => (
                  <div key={job.id} style={{
                    padding: "10px 14px", borderBottom: "1px solid #f5f5f5",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    fontSize: 12,
                  }}>
                    <div>
                      <strong>{job.salesRepName || "—"}</strong>
                      {" · "}
                      <span style={{ color: "#666" }}>{job.brokerName}</span>
                      {" · "}
                      <span style={{ color: "#888" }}>{job.scheduledTime || ""}</span>
                    </div>
                    <span style={{ fontSize: 11, color: "#888" }}>
                      {(job.guardAssignments || []).map((g) => g.guardName?.split(" ")[0] || `G${g.guardNumber}`).join(", ") || "Pool"}
                    </span>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}
      </div>

      {/* Read-only job preview opened by clicking "Open" on a draft
          invoice. Same modal used on Job Inbox / Invoices. */}
      {viewJobId && (
        <JobDetailModal
          jobId={viewJobId}
          companyId={userProfile?.companyId}
          onClose={() => setViewJobId(null)}
        />
      )}
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────
function KpiCard({ label, value, hint, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff", border: "1px solid #e8e8e8", borderRadius: 10,
        padding: "16px 18px", cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) => onClick && (e.currentTarget.style.borderColor = "#c0d4eb")}
      onMouseLeave={(e) => onClick && (e.currentTarget.style.borderColor = "#e8e8e8")}
    >
      <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color, lineHeight: 1.1 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
        {hint}
      </div>
    </div>
  );
}
